import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Plus, Trash2, Building2, Loader2, AlertTriangle, Check } from 'lucide-react';

/**
 * LegacyClientRelationshipsCard
 *
 * Admin-only editor that lets an operator log a per-client pre-platform
 * history for a talent. Each row creates an entry in
 * `talent_legacy_client_relationships` (V113) — the same table the
 * recommendation engine unions into platform bookings to drive the
 * "Worked with you before" tag.
 *
 * Client picker: typeahead against `/api/admin/users?role=client&search=`.
 * Keeping it as a simple text-search Dialog (not a full select) since admins
 * know the company name + can paste from another tab quickly.
 */

const LegacyClientRelationshipsCard = ({ talentId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [count, setCount] = useState('1');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/talent/${talentId}/legacy-clients`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load legacy client relationships');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (talentId) load(); /* eslint-disable-next-line */ }, [talentId]);

  const searchClients = async (q) => {
    setSearch(q);
    if (!q || q.trim().length < 2) {
      setCandidates([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(
        `/admin/users?role=client&search=${encodeURIComponent(q.trim())}&limit=10`,
      );
      const users = Array.isArray(data) ? data : (data.users || []);
      setCandidates(users);
    } catch {
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  };

  const openDialog = () => {
    setDialogOpen(true);
    setSelected(null); setSearch(''); setCandidates([]); setCount('1'); setNotes('');
  };

  const saveRelationship = async () => {
    if (!selected) { toast.error('Pick a client first'); return; }
    setSaving(true);
    try {
      await api.post(`/admin/talent/${talentId}/legacy-clients`, {
        client_user_id: selected.id,
        past_events_count: Math.max(1, parseInt(count || '1', 10)),
        notes: notes.trim() || null,
      });
      toast.success('Legacy relationship saved');
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteRelationship = async (relId) => {
    if (!window.confirm('Remove this legacy client relationship?')) return;
    try {
      await api.delete(`/admin/talent/${talentId}/legacy-clients/${relId}`);
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid="legacy-client-rels-card">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-cyan-600" />
            Legacy Client Relationships
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1 max-w-prose">
            Record past working relationships with specific clients (pre-platform or off-platform).
            These feed the &ldquo;Worked with you before&rdquo; tag and the recommendation boost.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={openDialog}
          className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
          data-testid="legacy-client-rel-add"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-3 italic">
            No legacy relationships logged. Platform bookings already tag automatically.
          </p>
        ) : (
          <div className="divide-y">
            {rows.map(r => (
              <div key={r.id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`legacy-rel-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {r.company_name || r.contact_name || r.client_email}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    <Badge className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
                      {r.past_events_count} past event{r.past_events_count === 1 ? '' : 's'}
                    </Badge>
                    <span className="text-[11px] text-slate-400">{r.client_email}</span>
                  </div>
                  {r.notes && <p className="text-xs text-slate-600 mt-1 italic">&ldquo;{r.notes}&rdquo;</p>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteRelationship(r.id)}
                  className="text-red-600 hover:bg-red-50"
                  data-testid={`legacy-rel-delete-${r.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="legacy-client-rel-dialog">
          <DialogHeader>
            <DialogTitle>Add Legacy Client Relationship</DialogTitle>
            <DialogDescription>
              Search for an existing client account and log past events with them.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Client (search by email / company)</label>
              <Input
                value={search}
                onChange={(e) => searchClients(e.target.value)}
                placeholder="e.g. acme or contact@company.com"
                data-testid="legacy-client-rel-search"
              />
              {searching && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mt-2" />}
              {!searching && candidates.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-md mt-2">
                  {candidates.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setSelected(c); setCandidates([]); setSearch(c.email); }}
                      className="w-full text-left px-3 py-2 hover:bg-cyan-50 border-b last:border-0 text-sm"
                    >
                      <p className="font-medium">{c.company_name || c.contact_name || c.email}</p>
                      <p className="text-xs text-slate-500">{c.email}</p>
                    </button>
                  ))}
                </div>
              )}
              {!searching && search.length >= 2 && candidates.length === 0 && !selected && (
                <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> No client found. They must have a user account on the platform.
                </p>
              )}
              {selected && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-sm flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span>Selected: <strong>{selected.email}</strong></span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Past events with this client</label>
              <Input
                type="number"
                min={1}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                data-testid="legacy-client-rel-count"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Three summer activations in 2023"
                data-testid="legacy-client-rel-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveRelationship}
              disabled={saving || !selected}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="legacy-client-rel-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save relationship
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default LegacyClientRelationshipsCard;
